export interface OklchColor {
  l: number;
  c: number;
  h: number;
  alpha: number;
}

export interface RgbColor {
  r: number;
  g: number;
  b: number;
  alpha: number;
}

export interface HslColor {
  h: number;
  s: number;
  l: number;
  alpha: number;
}

export interface HsbColor {
  h: number;
  s: number;
  b: number;
  alpha: number;
}

const D65 = {
  x: 0.95047,
  y: 1,
  z: 1.08883,
};

export function oklchColor(l: number, c: number, h: number, alpha = 1): OklchColor {
  return {
    l: clamp(l, 0, 1),
    c: clamp(c, 0, 0.5),
    h: normalizeHue(h),
    alpha: clamp(alpha, 0, 1),
  };
}

import { tryParseColorString } from "./colorString";

export function parseColor(value: string | OklchColor): OklchColor {
  if (typeof value !== "string") {
    return oklchColor(value.l, value.c, value.h, value.alpha ?? 1);
  }

  return tryParseColorString(value) ?? oklchColor(0.75, 0.12, 220);
}

export function oklchToCss(color: OklchColor): string {
  const normalized = parseColor(color);
  return `oklch(${round(normalized.l * 100, 2)}% ${round(normalized.c, 4)} ${round(normalized.h, 2)} / ${round(normalized.alpha, 3)})`;
}

export function oklchToHex(color: OklchColor): string {
  const rgb = oklchToRgb(color);
  return `#${toHexByte(rgb.r)}${toHexByte(rgb.g)}${toHexByte(rgb.b)}`;
}

export function oklchToRgba(color: OklchColor): [number, number, number, number] {
  const rgb = oklchToRgb(color);
  return [rgb.r / 255, rgb.g / 255, rgb.b / 255, rgb.alpha];
}

export function interpolateRgb(from: OklchColor, to: OklchColor, t: number): OklchColor {
  const start = oklchToRgb(from);
  const end = oklchToRgb(to);
  return rgbToOklch({
    r: lerp(start.r, end.r, t),
    g: lerp(start.g, end.g, t),
    b: lerp(start.b, end.b, t),
    alpha: lerp(start.alpha, end.alpha, t),
  });
}

export function interpolateOklch(from: OklchColor, to: OklchColor, t: number): OklchColor {
  const start = parseColor(from);
  const end = parseColor(to);
  const hueDelta = shortestHueDelta(start.h, end.h);
  return oklchColor(
    lerp(start.l, end.l, t),
    lerp(start.c, end.c, t),
    start.h + hueDelta * clamp01(t),
    lerp(start.alpha, end.alpha, t),
  );
}

export function interpolatePremultipliedGradientColor(from: OklchColor, to: OklchColor, t: number): OklchColor {
  const start = oklchToRgb(parseColor(from));
  const end = oklchToRgb(parseColor(to));
  const mixedAlpha = lerp(start.alpha, end.alpha, t);
  if (mixedAlpha <= 0) {
    return oklchColor(0, 0, 0, 0);
  }

  const r = lerp(start.r * start.alpha, end.r * end.alpha, t) / mixedAlpha;
  const g = lerp(start.g * start.alpha, end.g * end.alpha, t) / mixedAlpha;
  const b = lerp(start.b * start.alpha, end.b * end.alpha, t) / mixedAlpha;
  return rgbToOklch({ r, g, b, alpha: mixedAlpha });
}

export function interpolateGradientColor(
  from: OklchColor,
  to: OklchColor,
  t: number,
  colorSpace: "oklch" | "rgb",
): OklchColor {
  const start = parseColor(from);
  const end = parseColor(to);
  if (start.alpha < 1 || end.alpha < 1 || start.alpha !== end.alpha) {
    return interpolatePremultipliedGradientColor(start, end, t);
  }

  return colorSpace === "oklch" ? interpolateOklch(start, end, t) : interpolateRgb(start, end, t);
}

export function rgbToOklch(rgb: RgbColor): OklchColor {
  const linearR = srgbToLinear(rgb.r / 255);
  const linearG = srgbToLinear(rgb.g / 255);
  const linearB = srgbToLinear(rgb.b / 255);

  const x = linearR * 0.4124564 + linearG * 0.3575761 + linearB * 0.1804375;
  const y = linearR * 0.2126729 + linearG * 0.7151522 + linearB * 0.072175;
  const z = linearR * 0.0193339 + linearG * 0.119192 + linearB * 0.9503041;

  const lmsL = Math.cbrt(0.8189330101 * (x / D65.x) + 0.3618667424 * (y / D65.y) - 0.1288597137 * (z / D65.z));
  const lmsM = Math.cbrt(0.0329845436 * (x / D65.x) + 0.9293118715 * (y / D65.y) + 0.0361456387 * (z / D65.z));
  const lmsS = Math.cbrt(0.0482003018 * (x / D65.x) + 0.2643662691 * (y / D65.y) + 0.633851707 * (z / D65.z));

  const labL = 0.2104542553 * lmsL + 0.793617785 * lmsM - 0.0040720468 * lmsS;
  const labA = 1.9779984951 * lmsL - 2.428592205 * lmsM + 0.4505937099 * lmsS;
  const labB = 0.0259040371 * lmsL + 0.7827717662 * lmsM - 0.808675766 * lmsS;

  return oklchColor(labL, Math.hypot(labA, labB), Math.atan2(labB, labA) * 180 / Math.PI, rgb.alpha);
}

export function oklchToRgb(color: OklchColor): RgbColor {
  const normalized = parseColor(color);
  const hueRadians = normalized.h * Math.PI / 180;
  const labA = normalized.c * Math.cos(hueRadians);
  const labB = normalized.c * Math.sin(hueRadians);

  const lmsL = normalized.l + 0.3963377774 * labA + 0.2158037573 * labB;
  const lmsM = normalized.l - 0.1055613458 * labA - 0.0638541728 * labB;
  const lmsS = normalized.l - 0.0894841775 * labA - 1.291485548 * labB;

  const l = lmsL ** 3;
  const m = lmsM ** 3;
  const s = lmsS ** 3;

  const x = D65.x * (1.2270138511 * l - 0.5577999807 * m + 0.281256149 * s);
  const y = D65.y * (-0.0405801784 * l + 1.1122568696 * m - 0.0716766787 * s);
  const z = D65.z * (-0.0763812845 * l - 0.4214819784 * m + 1.5861632204 * s);

  const linearR = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z;
  const linearG = -0.969266 * x + 1.8760108 * y + 0.041556 * z;
  const linearB = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z;

  return {
    r: Math.round(clamp(linearToSrgb(linearR), 0, 1) * 255),
    g: Math.round(clamp(linearToSrgb(linearG), 0, 1) * 255),
    b: Math.round(clamp(linearToSrgb(linearB), 0, 1) * 255),
    alpha: normalized.alpha,
  };
}

export function hslToOklch(color: HslColor): OklchColor {
  return rgbToOklch(hslToRgb(color));
}

export function oklchToHsl(color: OklchColor): HslColor {
  return rgbToHsl(oklchToRgb(color));
}

export function hsbToOklch(color: HsbColor): OklchColor {
  return rgbToOklch(hsbToRgb(color));
}

export function oklchToHsb(color: OklchColor): HsbColor {
  return rgbToHsb(oklchToRgb(color));
}

export function hexToRgb(hex: string): RgbColor {
  const normalized = hex.replace("#", "").trim();
  const expanded = normalized.length === 3 || normalized.length === 4
    ? normalized.split("").map(character => `${character}${character}`).join("")
    : normalized;
  const value = Number.parseInt(expanded.padEnd(6, "0").slice(0, 6), 16);
  const alpha = expanded.length >= 8
    ? Number.parseInt(expanded.slice(6, 8), 16) / 255
    : 1;
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
    alpha: clamp(alpha, 0, 1),
  };
}

export function rgbToHsl(rgb: RgbColor): HslColor {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  return {
    h: rgbHue(r, g, b, max, delta),
    s: saturation,
    l: lightness,
    alpha: rgb.alpha,
  };
}

export function rgbToHsb(rgb: RgbColor): HsbColor {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  return {
    h: rgbHue(r, g, b, max, delta),
    s: max === 0 ? 0 : delta / max,
    b: max,
    alpha: rgb.alpha,
  };
}

export function hslToRgb(color: HslColor): RgbColor {
  const h = normalizeHue(color.h) / 60;
  const c = (1 - Math.abs(2 * color.l - 1)) * color.s;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const m = color.l - c / 2;
  const [r, g, b] = hueToRgbTuple(h, c, x);
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255), alpha: color.alpha };
}

export function hsbToRgb(color: HsbColor): RgbColor {
  const h = normalizeHue(color.h) / 60;
  const c = color.b * color.s;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const m = color.b - c;
  const [r, g, b] = hueToRgbTuple(h, c, x);
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255), alpha: color.alpha };
}

export function round(value: number, digits = 0): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function rgbHue(r: number, g: number, b: number, max: number, delta: number): number {
  if (delta === 0) {
    return 0;
  }

  if (max === r) {
    return normalizeHue(60 * (((g - b) / delta) % 6));
  }

  if (max === g) {
    return 60 * ((b - r) / delta + 2);
  }

  return 60 * ((r - g) / delta + 4);
}

function hueToRgbTuple(h: number, c: number, x: number): [number, number, number] {
  if (h < 1) return [c, x, 0];
  if (h < 2) return [x, c, 0];
  if (h < 3) return [0, c, x];
  if (h < 4) return [0, x, c];
  if (h < 5) return [x, 0, c];
  return [c, 0, x];
}

function srgbToLinear(value: number): number {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(value: number): number {
  return value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;
}

function normalizeHue(hue: number): number {
  return ((hue % 360) + 360) % 360;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * clamp01(t);
}

function shortestHueDelta(from: number, to: number): number {
  const delta = normalizeHue(to - from);
  return delta > 180 ? delta - 360 : delta;
}

function toHexByte(value: number): string {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0");
}
